import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// Configuraciones page components
import ConfiguracionesInformation from "layouts/asistencia/configuraciones/components/ConfiguracionesInformation"

// React Router
import { Link, useNavigate } from "react-router-dom"

// Config and JWT
import config from "config"
import { jwtDecode } from "jwt-decode"

// Services
import {
  fetchConfiguraciones,
  fetchDependencias,
  fetchTiposAsistente,
} from "layouts/asistencia/configuraciones/components/add/service"

function Configuraciones() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  // Data states
  const [configuraciones, setConfiguraciones] = useState([])
  const [dependencias, setDependencias] = useState([])
  const [tiposAsistente, setTiposAsistente] = useState([])

  // Loading and pagination
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  // Filters
  const [selectedOrganizacion, setSelectedOrganizacion] = useState("")
  const [selectedTipoAsistente, setSelectedTipoAsistente] = useState("")
  const [selectedDiaSemana, setSelectedDiaSemana] = useState("")
  const [filterActivo, setFilterActivo] = useState(true)

  const diasSemana = [
    { value: "LUNES", label: "Lunes" },
    { value: "MARTES", label: "Martes" },
    { value: "MIERCOLES", label: "Miércoles" },
    { value: "JUEVES", label: "Jueves" },
    { value: "VIERNES", label: "Viernes" },
    { value: "SABADO", label: "Sábado" },
    { value: "DOMINGO", label: "Domingo" },
  ]

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadDependencias()
    loadTiposAsistente()
    loadConfiguraciones(0)
  }, [])

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias()
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading organizaciones:", error)
    }
  }

  const loadTiposAsistente = async () => {
    try {
      const data = await fetchTiposAsistente()
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
    }
  }

  const loadConfiguraciones = async (page) => {
    setLoading(true)
    try {
      const params = {}

      if (selectedOrganizacion) {
        params.dependenciaId = selectedOrganizacion
      }

      if (selectedTipoAsistente) {
        params.tipoAsistenteId = selectedTipoAsistente
      }

      if (selectedDiaSemana) {
        params.diaSemana = selectedDiaSemana
      }

      params.activo = filterActivo

      const result = await fetchConfiguraciones(params)

      if (Array.isArray(result)) {
        setConfiguraciones(result)
        setTotalPages(1)
        setTotalElements(result.length)
      } else {
        setConfiguraciones([])
        setTotalPages(0)
        setTotalElements(0)
      }
    } catch (error) {
      console.error("Error loading configuraciones:", error)
      setConfiguraciones([])
    } finally {
      setLoading(false)
    }
  }

  const gotoPage = (p) => {
    setPageIndex(p)
  }

  const reloadConfiguraciones = () => {
    loadConfiguraciones(pageIndex)
  }

  const handleBuscar = () => {
    setPageIndex(0)
    loadConfiguraciones(0)
  }

  const handleClearFilters = () => {
    setSelectedOrganizacion("")
    setSelectedTipoAsistente("")
    setSelectedDiaSemana("")
    setFilterActivo(true)
    setPageIndex(0)
    setConfiguraciones([])
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              {/* Filtros */}
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={handleExpandClick}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      {expanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      {/* Dependencia */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Dependencia</InputLabel>
                          <Select
                            label="Dependencia"
                            variant="outlined"
                            fullWidth
                            value={selectedOrganizacion}
                            onChange={(e) => setSelectedOrganizacion(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todas</MenuItem>
                            {dependencias.map((org) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Tipo de Asistente */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo de Asistente</InputLabel>
                          <Select
                            label="Tipo de Asistente"
                            variant="outlined"
                            fullWidth
                            value={selectedTipoAsistente}
                            onChange={(e) => setSelectedTipoAsistente(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {tiposAsistente.map((tipo) => (
                              <MenuItem key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Día de Semana */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Día de Semana</InputLabel>
                          <Select
                            label="Día de Semana"
                            variant="outlined"
                            fullWidth
                            value={selectedDiaSemana}
                            onChange={(e) => setSelectedDiaSemana(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {diasSemana.map((dia) => (
                              <MenuItem key={dia.value} value={dia.value}>
                                {dia.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Activo */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filterActivo}
                              onChange={(e) => setFilterActivo(e.target.checked)}
                            />
                          }
                          label="Solo activos"
                        />
                      </Grid>

                      {/* Botones */}
                      <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <MDBox>
                            <MDButton type="submit" variant="gradient" color="info" onClick={handleBuscar}>
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton type="button" variant="outlined" color="info" onClick={handleClearFilters}>
                              Limpiar
                            </MDButton>
                          </MDBox>

                          <Link to="/asistencia/configuraciones/agregar" style={{ textDecoration: "none" }}>
                            <MDButton type="button" variant="gradient" color="success">
                              Agregar
                            </MDButton>
                          </Link>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>

              <Divider />

              {/* Tabla de Configuraciones */}
              <Card pt={5} mt={3}>
                {/* Contenido */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : configuraciones && configuraciones.length > 0 ? (
                  <ConfiguracionesInformation
                    configuraciones={configuraciones}
                    onReload={reloadConfiguraciones}
                  />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Configuraciones
